import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Breadcrumbs from '../components/Breadcrumbs';
import '../App.css';
import '../components/SearchResults.css';
import './DetailPage.css';

interface JOArticle {
    id: number;
    numero?: string;
    title?: string;
    titlefr?: string;
    titlear?: string;
    lienpdffr?: string;
    lienpdfar?: string;
    annee?: number;
    jo_numero?: string;
    date_publication?: string;
    date_signature?: string;
    natureid?: number;
    type?: string;
    modifref?: string;
    modifdate?: string;
    modifnature?: string;
    applicref?: string;
    applicdate?: string;
    applicnature?: string;
    abrogation?: string;
    abrogref?: string;
    abrogobs?: string;
    abrogobs_ar?: string;
    contenu?: string;
    contenuar?: string;
    nature?: string;
    nature_ar?: string;
    // History
    modifications?: any[];
    applications?: any[];
    abrogations?: any[];
}

const JOArticleDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [article, setArticle] = useState<JOArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Timeline & Modal State
    const [historyItems, setHistoryItems] = useState<any[]>([]);
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchArticle();
    }, [id, i18n.language]);

    const fetchArticle = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/joarticle/${id}?lang=${i18n.language}`);
            const data = response.data;
            setArticle(data);

            // Merge and sort history
            const mergedHistory = [
                ...(data.modifications || []).map((item: any) => ({ ...item, type: 'modification', dateObj: new Date(item.datesignature || item.date_publication) })),
                ...(data.applications || []).map((item: any) => ({ ...item, type: 'application', dateObj: new Date(item.datesignature || item.date_publication) })),
                ...(data.abrogations || []).map((item: any) => ({ ...item, type: 'abrogation', dateObj: new Date(item.datesignature || item.date_publication) }))
            ];

            // Sort by date descending (newest first)
            mergedHistory.sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
            setHistoryItems(mergedHistory);

        } catch (err) {
            console.error('Error fetching JO article:', err);
            setError('Erreur lors du chargement de l\'article JO');
        } finally {
            setLoading(false);
        }
    };

    const handleTimelineClick = (item: any) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">{error || 'Article JO non trouvé'}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                </button>
            </div>
        );
    }

    const displayTitle = i18n.language === 'ar'
        ? (article.titlear || article.titlefr || article.title)
        : (article.titlefr || article.title || article.titlear);

    const nature = i18n.language === 'ar'
        ? article.nature_ar
        : article.nature;

    const pdfLink = i18n.language === 'ar'
        ? (article.lienpdfar || article.lienpdffr)
        : (article.lienpdffr || article.lienpdfar);

    // Language availability check
    const isArabic = i18n.language === 'ar';
    const hasContentInCurrentLang = isArabic
        ? !!(article.contenuar || article.titlear)?.trim()
        : !!(article.contenu || article.titlefr)?.trim();
    const hasContentInOtherLang = isArabic
        ? !!(article.contenu || article.titlefr)?.trim()
        : !!(article.contenuar || article.titlear)?.trim();
    const showLangWarning = !hasContentInCurrentLang && hasContentInOtherLang;

    const breadcrumbs = [
        {
            label: t('search.types.joarticles'),
            onClick: () => navigate(-1)
        },
        { label: `${t('detail.jo')} N° ${article.numero}` }
    ];

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(isArabic ? 'ar-DZ' : 'fr-FR');
    };

    // Source de publication : « Publié au Journal officiel n° X du JJ mois AAAA »
    const joNumero = article.jo_numero?.trim();
    const joSource = joNumero
        ? (article.date_publication
            ? t('detail.joSource', {
                num: joNumero,
                date: new Date(article.date_publication).toLocaleDateString(isArabic ? 'ar-DZ' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
            })
            : t('detail.joSourceNoDate', { num: joNumero }))
        : null;

    const getTypeText = (type: string) => {
        switch (type) {
            case 'modification': return isArabic ? 'تعديل' : 'Modification';
            case 'application': return isArabic ? 'تطبيق' : 'Application';
            case 'abrogation': return isArabic ? 'إلغاء' : 'Abrogation';
            default: return type;
        }
    };

    // Calculate dynamic abrogation status from history
    const hasAbrogations = article.abrogations && article.abrogations.length > 0;
    const isPartialAbrogation = hasAbrogations && article.abrogations?.some((item: any) =>
        (item.obs && item.obs.toLowerCase().includes('partiel')) ||
        (item.nature && item.nature.toLowerCase().includes('partiel'))
    );

    return (
        <div className="detail-page">
            <Breadcrumbs items={breadcrumbs} />

            {/* Card 1: Attributes / Metadata */}
            <div className="result-card type-jo mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <span className="premium-badge badge-jo mb-2 d-inline-block">
                                <i className="bi bi-newspaper me-2"></i>{t('detail.jo')}
                            </span>
                            {hasAbrogations && (
                                <span className={`premium-badge badge-jo mb-2 d-inline-block bg-danger text-light mx-2`}>
                                    <i className="bi bi-x-circle me-1"></i>
                                    {isArabic
                                        ? (isPartialAbrogation ? 'إلغاء جزئي' : 'إلغاء كلي')
                                        : (isPartialAbrogation ? 'Abrogation partielle' : 'Abrogation totale')}
                                </span>
                            )}
                            <h1 className="h3 fw-bold text-dark mb-2 result-title" style={{ fontSize: '1.75rem' }}>
                                {displayTitle}
                            </h1>
                            {joSource && (
                                <p className="mb-0 fw-semibold text-secondary">
                                    <i className="bi bi-newspaper me-2"></i>{joSource}
                                </p>
                            )}
                        </div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                        </button>
                    </div>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="row g-4">
                        {article.numero && (
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="text-secondary me-3">
                                        <i className="bi bi-hash fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.number')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{article.numero}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {(article.nature || article.nature_ar) && (
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="text-secondary me-3 ms-2">
                                        <i className="bi bi-tag fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.legalNature')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{nature}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        {article.annee && (
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="text-secondary me-3 ms-2">
                                        <i className="bi bi-calendar fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('search.joPublicationYear')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{article.annee}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Date fields with small text and icons */}
                        {article.date_publication && (
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="text-secondary me-2 ms-2">
                                        <i className="bi bi-calendar-check fs-5"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{t('search.joPublicationDate')}</small>
                                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{new Date(article.date_publication).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR')}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {article.date_signature && (
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="text-secondary me-2 ms-2">
                                        <i className="bi bi-pen-fill fs-5"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{t('search.joSignatureDate')}</small>
                                        <span className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{new Date(article.date_signature).toLocaleDateString(i18n.language === 'ar' ? 'ar-DZ' : 'fr-FR')}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>


                    <hr className="my-4 text-muted opacity-25" />

                    {historyItems.length > 0 && (
                        <div className="row g-4">
                            <div className="col-md-12">
                                <div className="p-3 border-bottom bg-light">
                                    <h5 className="mb-0"><i className="bi bi-clock-history me-2"></i>{isArabic ? 'تاريخ النصوص المرتبطة' : 'Evolution'}</h5>
                                </div>

                                <ul className="list-group list-group-flush mt-3">
    {historyItems.map((item, index) => (
        <li
            key={`${item.type}-${item.id}-${index}`}
            className="list-group-item list-group-item-action d-flex align-items-start gap-3"
            role="button"
            onClick={() => handleTimelineClick(item)}
        >
            <div className="pt-1">
                <i
                    className={`fs-5 ${
                        item.type === 'modification'
                            ? 'bi bi-pencil-square text-warning'
                            : item.type === 'application'
                            ? 'bi bi-check-circle text-success'
                            : 'bi bi-x-circle text-danger'
                    }`}
                ></i>
            </div>

            <div className="flex-grow-1">
                <div className="fw-semibold">
                    {isArabic
                        ? (item.titlear || item.title)
                        : (item.title || item.titlear)}
                </div>

                <div className="text-muted small">
                    {getTypeText(item.type)} • {formatDate(item.datesignature || item.date_publication)}
                </div>
            </div>

            <i className="bi bi-chevron-right text-muted pt-1"></i>
        </li>
    ))}
</ul>

                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Language availability warning */}
            {showLangWarning && (
                <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4">
                    <span>
                        <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                        {isArabic ? 'غير متوفر بالعربية' : 'Non disponible en français'}
                    </span>
                    <Link
                        to={`/${isArabic ? 'fr' : 'ar'}/detail/jo/${id}`}
                        className="btn btn-warning"
                    >
                        {isArabic ? 'Voir en français' : 'عرض بالعربية'}
                    </Link>
                </div>
            )}

            {/* Card 2: Content */}
            {(article.contenu || article.contenuar) && (
                <div className="result-card type-jo mb-4 animate-fade-in">
                    <div className="result-card-body p-4">
                        <h5 className="mb-3 d-flex align-items-center">
                            <i className="bi bi-file-text me-2"></i>
                            {t('detail.content')}
                        </h5>
                        <div className="content-text" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                            {i18n.language === 'ar' && article.contenuar ? (
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.contenuar) }} />
                            ) : (
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.contenu || article.contenuar || '') }} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Card 3: Actions & Additional Info */}
            <div className="result-card type-jo animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="text-end text-muted small mt-4">
                        <span className="bg-light px-3 py-1 rounded-pill border">
                            <i className="bi bi-hash me-1"></i>ID: {article.id}
                        </span>
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            {showModal && selectedItem && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog" onClick={closeModal}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={e => e.stopPropagation()}>
                            <div className="modal-content timeline-modal-content">
                                <div className="modal-header timeline-modal-header">
                                    <h5 className="modal-title fw-bold">
                                        <i className={`bi me-2 ${selectedItem.type === 'modification' ? 'bi-pencil-square text-warning' :
                                            selectedItem.type === 'application' ? 'bi-check-circle text-success' :
                                                'bi-x-circle text-danger'
                                            }`}></i>
                                        {getTypeText(selectedItem.type)}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                                </div>
                                <div className="modal-body timeline-modal-body">
                                    <div className="mb-4">
                                        <label className="text-secondary small fw-bold text-uppercase mb-1">{t('detail.title')}</label>
                                        <p className="fs-5 fw-medium text-dark mb-0">
                                            {isArabic ? (selectedItem.titlear || selectedItem.title) : (selectedItem.title || selectedItem.titlear)}
                                        </p>
                                    </div>

                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6">
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-secondary d-block mb-1">{t('detail.number')}</small>
                                                <strong>{selectedItem.numero}</strong>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-secondary d-block mb-1">{isArabic ? 'التاريخ' : 'Date'}</small>
                                                <strong>{formatDate(selectedItem.datesignature || selectedItem.date_publication)}</strong>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="p-3 bg-light rounded-3">
                                                <small className="text-secondary d-block mb-1">{t('detail.legalNature')}</small>
                                                <strong>{isArabic ? selectedItem.nature_ar : selectedItem.nature}</strong>
                                            </div>
                                        </div>
                                    </div>

                                    {selectedItem.obs && (
                                        <div className="alert alert-info border-0 shadow-sm d-flex align-items-start">
                                            <i className="bi bi-info-circle-fill me-3 mt-1 fs-5"></i>
                                            <div>
                                                <strong className="d-block mb-1">{isArabic ? 'ملاحظات' : 'Observations'}</strong>
                                                {selectedItem.obs}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4 text-end">
                                        <Link
                                            to={`/${i18n.language}/detail/jo/${selectedItem.id}`}
                                            className="btn btn-primary rounded-pill px-4"
                                            onClick={closeModal}
                                        >
                                            {isArabic ? 'عرض النص الكامل' : 'Voir le texte complet'} <i className="bi bi-arrow-right ms-2"></i>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default JOArticleDetail;
